import Api from '@/api'

export default {
  namespaced: true,
  state: {
    emailNotificationSetting: false,
    reminderSetting: false,
    scheduleList: [],
    notifications: [],
    unreadNotifications: [],
    // The true total unread count (from the paginated response's meta.total)
    // — unreadNotifications itself is capped at whatever per_page was
    // requested (10 by default), so its .length alone silently undercounts
    // once there are more than that many unread, capping the bell badge at
    // 10 forever regardless of the real number.
    unreadCount: 0,
  },

  mutations: {
    setEmailNotificationSetting(state, emailNotificationSetting) {
      state.emailNotificationSetting = emailNotificationSetting
    },
    setReminderSetting(state, reminderSetting) {
      state.reminderSetting = reminderSetting
    },
    setScheduleList(state, scheduleList) {
      state.scheduleList = scheduleList
    },
    setNotifications(state, notifications) {
      state.notifications = notifications
    },
    setUnreadNotifications(state, unreadNotifications) {
      state.unreadNotifications = unreadNotifications
    },
    setUnreadCount(state, unreadCount) {
      state.unreadCount = unreadCount
    },
  },

  actions: {
    async getNotificationSettings({ commit }) {
      const { data } = await Api.get(`notifications/settings`)
      commit(
        'setEmailNotificationSetting',
        Boolean(Number(data.data.email_notification_enable)),
      )
      commit('setReminderSetting', data.data.expiring_schedule_id.label)
    },
    async getScheduleList({ commit }) {
      const { data } = await Api.get(`notifications/settings/schedule`)
      commit('setScheduleList', data.data)
    },
    async getNotifications({ commit }, perPage = 50) {
      const { data } = await Api.get(`notifications?per_page=${perPage}`)
      commit('setNotifications', data.data)
    },
    async getUnreadNotifications({ commit }, perPage = 10) {
      const { data } = await Api.get(`notifications/unread?per_page=${perPage}`)
      commit('setUnreadNotifications', data.data)
      // data.data.length is capped at perPage — meta.total is the real count
      // across every page, which is what the bell badge actually needs.
      commit('setUnreadCount', (data.meta && data.meta.total) || 0)
    },
    /**
     * GET notifications/sent — MassNotificationController@index (company-scoped).
     * Params: page, per_page, filter[project_id], filter[sender_id], filter[title] (title search; backend).
     */
    async getSentMassLogs(_, { page = 1, limit = 10, filters = null } = {}) {
      const params = {
        page,
        per_page: limit,
      }
      if (filters && typeof filters === 'object') {
        if (filters.project_id != null && filters.project_id !== '') {
          params['filter[project_id]'] = filters.project_id
        }
        if (filters.sender_id != null && filters.sender_id !== '') {
          params['filter[sender_id]'] = filters.sender_id
        }
        const title = filters.title != null ? String(filters.title).trim() : ''
        if (title) {
          params['filter[title]'] = title
        }
      }

      const { data } = await Api.get('notifications/sent', { params })
      return data
    },
    async deleteNotification({ state, commit, dispatch }, id) {
      // Drop it from the lists and the bell count straight away, so the badge
      // moves the moment "x" is clicked rather than after the round trips.
      // The unread list is capped, so also check the panel's own list.
      const wasUnread =
        state.unreadNotifications.some((n) => n.id === id) ||
        state.notifications.some((n) => n.id === id && n.read === false)
      commit(
        'setNotifications',
        state.notifications.filter((n) => n.id !== id),
      )
      commit(
        'setUnreadNotifications',
        state.unreadNotifications.filter((n) => n.id !== id),
      )
      if (wasUnread)
        commit('setUnreadCount', Math.max(state.unreadCount - 1, 0))
      try {
        await Api.delete(`notifications/${id}`)
      } finally {
        // Re-sync with the server: confirms the count, or restores the
        // notification if the delete failed.
        await Promise.all([
          dispatch('getNotifications'),
          dispatch('getUnreadNotifications'),
        ])
      }
    },
    async deleteAllNotifications({ dispatch }) {
      await Api.delete('notifications')
      await dispatch('getNotifications')
      await dispatch('getUnreadNotifications')
    },
    async getSentMassLogById(_, id) {
      const { data } = await Api.get(`notifications/sent/${id}`)
      return data
    },
  },

  getters: {
    emailNotificationSetting(state) {
      return state.emailNotificationSetting
    },
    reminderSetting(state) {
      return state.reminderSetting
    },
    scheduleList(state) {
      return state.scheduleList
    },
    notifications(state) {
      return state.notifications
    },
    unreadNotifications(state) {
      return state.unreadNotifications
    },
    unreadCount(state) {
      return state.unreadCount
    },
  },
}
